import { IconName } from "@components/LIcon";
import { AccessTypeOptions } from "@libs/utils";
import { Image } from "expo-image";
import { captureException } from "@utils/captureException";
import { getImageUrlWithSize } from "@utils/index";

type AccessHeroImageSource =
  | {
      type: "event";
      coverImageUrl?: string | null;
      artistImage?: string | null;
    }
  | {
      type: "music";
      artistImage?: string | null;
      songs?: {
        albums?: {
          image?: string | null;
        }[];
      }[];
    }
  | {
      type: "merch";
      imageUrl?: string | null;
      artistImage?: string | null;
    };

export const getMarketplacePercentageDiscountDisplayValue = (marketplace: {
  percentageDiscount: number | null;
}) => {
  return marketplace.percentageDiscount === null
    ? "none"
    : `${marketplace.percentageDiscount} %`;
};

export const getAccessTypeLogo = (accessType: AccessTypeOptions): IconName => {
  switch (accessType) {
    case "merch":
    case "event":
    case "music":
    case "post":
    case "experience":
    case "video":
    case "chat":
    default:
      return "logo_round_large";
  }
};

export const getAccessDetailHeroImage = (access: AccessHeroImageSource) => {
  switch (access.type) {
    case "event":
      return access.coverImageUrl ?? access.artistImage;
    case "music":
      return access.songs?.at(0)?.albums?.at(0)?.image ?? access.artistImage;
    // The product's picture, or the artist's, as on Link in Bio.
    case "merch":
      return access.imageUrl ?? access.artistImage;
  }
};

export const getAccessImage = getAccessDetailHeroImage;

const prefetchedAccessHeroUrls = new Set<string>();

export const prefetchAccessDetailHeroImage = (
  source: AccessHeroImageSource | string | null | undefined,
) => {
  const rawUrl =
    typeof source === "string" ? source : source && getAccessDetailHeroImage(source);
  const url = getImageUrlWithSize(rawUrl, "large");

  if (!url || prefetchedAccessHeroUrls.has(url)) return;

  prefetchedAccessHeroUrls.add(url);
  Image.prefetch(url, "disk").catch((error) => {
    prefetchedAccessHeroUrls.delete(url);
    captureException(error, {
      channel: "ignore",
      area: "image",
      operation: "prefetch-access-detail-hero-image",
    });
  });
};
